import React, { useEffect, useState, useCallback } from "react";
import { Plus, Copy, Trash2, Pencil } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { effectivePrice, money } from "@/lib/pricing";
import ProductForm from "./ProductForm";

const STOCK_OPTIONS = ["In Stock", "Low", "Sold Out"];

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);
  const loadCats = useCallback(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); loadCats(); }, [load, loadCats]);

  const patch = async (id, body) => {
    try { await api.patch(`/admin/products/${id}`, body); load(); } catch { toast.error("Update failed"); }
  };
  const duplicate = async (id) => { await api.post(`/admin/products/${id}/duplicate`); toast.success("Duplicated"); load(); };
  const remove = async (id) => { if (!window.confirm("Delete this product?")) return; await api.delete(`/admin/products/${id}`); toast.success("Deleted"); load(); };

  const openNew = () => { setEditing(null); setShowForm(true); };
  const openEdit = (p) => { setEditing(p); setShowForm(true); };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-heading font-black uppercase text-xl text-[#F0EDEA]">Products ({products.length})</h1>
        <button onClick={openNew} className="btn-skew btn-primary text-xs" data-testid="add-product-btn"><span><Plus size={16} /> Add</span></button>
      </div>

      <div className="space-y-3">
        {products.map((p) => (
          <div key={p.id} className="bg-[#1C1C1C] border border-[#333] rounded-xl p-3 flex gap-3" data-testid={`admin-product-${p.id}`}>
            <div className="w-16 h-16 rounded-lg overflow-hidden bg-black shrink-0">
              {p.media?.[0]
                ? (p.media[0].kind === "video" ? <video src={mediaUrl(p.media[0])} className="w-full h-full object-cover" muted /> : <img src={mediaUrl(p.media[0])} className="w-full h-full object-cover" alt="" />)
                : <img src="/logo_wide.webp" className="w-full h-full object-contain opacity-30" alt="" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-heading font-bold uppercase text-[#F0EDEA] text-sm truncate">{p.name} <span className="text-[#A8A2A0] font-normal">#{p.code}</span></h3>
                  <div className="text-[#E60000] font-bold text-sm">{money(effectivePrice(p))}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => openEdit(p)} className="p-1.5 text-[#A8A2A0] hover:text-[#E60000]" data-testid={`edit-product-${p.id}`}><Pencil size={16} /></button>
                  <button onClick={() => duplicate(p.id)} className="p-1.5 text-[#A8A2A0] hover:text-[#E60000]" data-testid={`dup-product-${p.id}`}><Copy size={16} /></button>
                  <button onClick={() => remove(p.id)} className="p-1.5 text-[#A8A2A0] hover:text-[#E60000]" data-testid={`del-product-${p.id}`}><Trash2 size={16} /></button>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <select value={p.stock} onChange={(e) => patch(p.id, { stock: e.target.value })} className="bg-[#141414] border border-[#333] rounded text-xs text-[#F0EDEA] px-2 py-1" data-testid={`stock-toggle-${p.id}`}>
                  {STOCK_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={() => patch(p.id, { visible: !p.visible })} className={`text-xs px-2 py-1 rounded border ${p.visible ? "border-green-600 text-green-400" : "border-[#333] text-[#A8A2A0]"}`} data-testid={`visible-toggle-${p.id}`}>
                  {p.visible ? "Visible" : "Hidden"}
                </button>
                <button onClick={() => patch(p.id, { hot_steal: !p.hot_steal })} className={`text-xs px-2 py-1 rounded border ${p.hot_steal ? "border-[#E60000] text-[#E60000]" : "border-[#333] text-[#A8A2A0]"}`} data-testid={`hot-toggle-${p.id}`}>
                  Hot Steal
                </button>
              </div>
            </div>
          </div>
        ))}
        {products.length === 0 && <div className="text-[#A8A2A0] text-center py-12">No products yet. Add your first one.</div>}
      </div>

      {showForm && (
        <ProductForm
          product={editing}
          categories={categories}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
          onCategoryAdded={loadCats}
        />
      )}
    </div>
  );
}
